import { TypographyStylesProvider } from "@mantine/core"
import { useMemo } from "react"
import type { TBlock, TInlineToken } from "./helpers"
import { parseBlocks, parseInline, safeHref } from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

const renderTokens = (tokens: TInlineToken[], keyPrefix: string): React.ReactNode[] =>
  tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`
    if (token.type === "text") return token.value
    if (token.type === "code") return <code key={key}>{token.value}</code>
    if (token.type === "bold") return <strong key={key}>{renderTokens(token.children, key)}</strong>
    if (token.type === "italic") return <em key={key}>{renderTokens(token.children, key)}</em>
    return (
      <a key={key} href={safeHref(token.href)} target="_blank" rel="noreferrer">
        {renderTokens(token.children, key)}
      </a>
    )
  })

const Heading = ({ level, children }: { level: number; children: React.ReactNode }): React.JSX.Element => {
  switch (level) {
    case 1:
      return <h1>{children}</h1>
    case 2:
      return <h2>{children}</h2>
    case 3:
      return <h3>{children}</h3>
    case 4:
      return <h4>{children}</h4>
    case 5:
      return <h5>{children}</h5>
    default:
      return <h6>{children}</h6>
  }
}

const renderBlock = (block: TBlock, index: number): React.ReactNode => {
  const key = `block-${index}`
  switch (block.type) {
    case "heading":
      return (
        <Heading key={key} level={block.level}>
          {renderTokens(parseInline(block.text), key)}
        </Heading>
      )
    case "ul":
      return (
        <ul key={key}>
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>{renderTokens(parseInline(item), `${key}-${itemIndex}`)}</li>
          ))}
        </ul>
      )
    case "ol":
      return (
        <ol key={key}>
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>{renderTokens(parseInline(item), `${key}-${itemIndex}`)}</li>
          ))}
        </ol>
      )
    case "paragraph":
      return <p key={key}>{renderTokens(parseInline(block.text), key)}</p>
  }
}

/**
 * Renders a constrained markdown subset (headings, lists, bold/italic, inline code, links) as
 * safe, semantic HTML. There is no `dangerouslySetInnerHTML` and no runtime HTML parsing — the
 * parser emits a data tree (see `helpers.ts`) that this component maps to React elements, and
 * Mantine's `TypographyStylesProvider` supplies the prose styling. Built for first-party content
 * such as desktop release notes; it is not a full CommonMark renderer.
 */
export const Markdown = ({ content, className }: IProps): React.JSX.Element => {
  const blocks = useMemo(() => parseBlocks(content), [content])
  const rootClassName = [classes.root, className].filter(Boolean).join(" ")

  return <TypographyStylesProvider className={rootClassName}>{blocks.map(renderBlock)}</TypographyStylesProvider>
}
